/**
 * HaulerCentral Token Provider
 *
 * Centralized token management for HaulerCentral authentication.
 * Handles token storage, retrieval, and validation.
 * Following patterns from @commander/auth.
 */

import { AUTH_STORAGE_KEYS } from '../constants/authConstants';
import type { AuthTokens } from '../types/auth.types';

/**
 * Store authentication tokens in localStorage
 */
export function setTokens(tokens: AuthTokens): void {
  localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
  localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
}

/**
 * Get current access token from localStorage
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
}

/**
 * Get current refresh token from localStorage
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
}

/**
 * Check if a valid access token exists
 * Note: This doesn't validate the token's expiry, just checks existence
 */
export function hasValidToken(): boolean {
  const token = getAccessToken();
  const isValid = token !== null && token.length > 0;
  return isValid;
}

/**
 * Check if token is expired (using JWT decode)
 */
export function isTokenExpired(token?: string): boolean {
  const tokenToCheck = token || getAccessToken();
  if (!tokenToCheck) return true;

  try {
    // Simple JWT decode without library
    const parts = tokenToCheck.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);

    // Add 30 second buffer
    return payload.exp < now - 30;
  } catch {
    return true;
  }
}

/**
 * Clear all authentication tokens from localStorage
 */
export function clearTokens(): void {
  localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
  localStorage.removeItem(AUTH_STORAGE_KEYS.AUTH_STATE);
}

/**
 * Store user data in localStorage
 */
export function setUserData(user: unknown): void {
  localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user));
}

/**
 * Get stored user data from localStorage
 */
export function getUserData(): unknown | null {
  const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Store auth tokens and user data together
 */
export function storeAuth(tokens: AuthTokens, user: unknown): void {
  setTokens(tokens);
  setUserData(user);
}

/**
 * Clear all auth-related data (tokens + user)
 */
export function clearAuth(): void {
  clearTokens();
}

/**
 * Get Authorization header value for API requests
 */
export function getAuthHeader(): Record<string, string> | null {
  const token = getAccessToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return null;
}

/**
 * Check if user appears to be authenticated
 * This is a client-side check and doesn't guarantee token is valid on the server
 */
export function isAuthenticated(): boolean {
  return hasValidToken();
}

/**
 * Handle token expired event
 * Clears auth and dispatches event for logout
 */
export function handleTokenExpired(): void {
  clearAuth();
  window.dispatchEvent(new CustomEvent('auth:token-expired'));
}

/**
 * Handle unauthorized response
 * Clears auth and dispatches event for logout
 */
export function handleUnauthorized(): void {
  clearAuth();
  window.dispatchEvent(new CustomEvent('auth:unauthorized'));
}

export default {
  setTokens,
  getAccessToken,
  getRefreshToken,
  hasValidToken,
  isTokenExpired,
  clearTokens,
  setUserData,
  getUserData,
  storeAuth,
  clearAuth,
  getAuthHeader,
  isAuthenticated,
  handleTokenExpired,
  handleUnauthorized,
};
